import { BaseModule } from "./BaseModule";
import { IModuleInitializerParams, ModuleInitializer } from "./ModuleInitializer";

/**
 * Type for a function that resolves a module class constructor.
 */
type ModuleResolver = () => Promise<new () => BaseModule>;

/**
 * Builder for creating module initializers with conditional loading.
 */
class ModuleInitializerBuilder {
    /**
     * Condition to determine if the module should be loaded.
     */
    private shouldLoadModule: (params: IModuleInitializerParams) => boolean = () => true; // default to always load

    /**
     * The loaded module instance.
     */
    private loadedModule: BaseModule | null = null;

    /**
     * Sets the condition for loading the module.
     * @param condition Function that takes initialization parameters and returns a boolean indicating if the module should be loaded.
     */
    public withConditionalLoading(condition: (params: IModuleInitializerParams) => boolean): this {
        this.shouldLoadModule = condition;
        return this;
    }

    /**
     * Builds the module initializer.
     * @param moduleName Name of the module.
     * @param resolveModule Function to resolve the module class.
     * @returns A function that initializes the module based on the provided parameters.
     */
    public build(moduleName: string, resolveModule: ModuleResolver): ModuleInitializer {
        return async (initializationParams: IModuleInitializerParams): Promise<boolean> => {
            try {
                const shouldLoad = this.shouldLoadModule(initializationParams);

                // unregister
                if (this.loadedModule && !shouldLoad) {
                    this.loadedModule.unregister();
                    this.loadedModule = null;
                    return false;
                }

                // load & register
                if (!this.loadedModule && shouldLoad) {
                    const Module = await resolveModule();
                    const moduleInstance = new Module();
                    moduleInstance.register();
                    this.loadedModule = moduleInstance;
                }

                return shouldLoad;
            }
            catch (error) {
                console.error(`Error initializing module ${moduleName}:`, error);
                return false;
            }
        };
    }
}

export { ModuleInitializerBuilder };
export type { ModuleResolver };
